/**
 * One movement in the table: five cells, the selection bar, and the three handlers that make the
 * row a pointer target, a focus target and the card's opener.
 *
 * ── Why it is its own component, which is a PERFORMANCE decision and was measured ───────────
 * The rows used to be rendered inline inside `ExerciseTable`'s own `rows.map`. Pointing at a row
 * changes `pointedKey` in the screen's state, so every one of those moves re-ran the whole map —
 * 479 rows times about a dozen elements each — to change a class on two of them. Measured on the
 * real catalogue on 18 September 2026, walking the pointer down 20 rows: 139 frames, 17 of them
 * over 33ms, the worst at 167ms, with 68 DOM mutations in the table and no image requests at all.
 * The cost was React reconciliation and nothing else.
 *
 * As a component, the React Compiler memoizes it on its props, so a move re-renders the two rows
 * whose `selected` actually flipped and the other 477 bail out. `pages/plan-library`'s `TemplateRow`
 * is the same shape for the same reason, and this screen's table is the one that was still inline.
 *
 * **That is why every prop here is a VALUE or a stable function.** `trainer` arrives as its two
 * strings rather than as the object the screen composes, and the pointer arrives as
 * `usePointedRow`'s `point` — a `useState` setter, stable by React's own guarantee — with the row
 * naming its own key at the call. A prop that is rebuilt on every render of the table would put all
 * the mounted rows back into the reconciler and undo the whole thing. `offset` is a NUMBER for that
 * reason: the table is virtual, every row is placed by the offset the virtualizer gives it, and a
 * ready-made `style` object handed down instead would be a fresh identity on every scroll frame.
 *
 * ── The handlers themselves are `ExerciseTable`'s rules, unchanged ──────────────────────────
 * `mousemove` rather than `mouseenter`, nothing at all on the row already selected, focus moving the
 * selection through `onFocusCapture`, and one click handler on the ROW rather than on the name's
 * button. The header of `ExerciseTable` is where all four are argued; they moved here with the
 * markup and none of them changed.
 */

import { useTranslation } from 'react-i18next';

import type { ExerciseRow } from '../model/exerciseRow';

import { ExerciseAuthorMark } from './ExerciseAuthorMark';
import {
  ExerciseEquipmentThumbs,
  ExerciseMuscles,
  ExerciseStill,
  ExerciseTags,
  ExerciseTypeMark,
} from './ExerciseCells';
import styles from './ExerciseTable.module.css';

interface ExerciseTableRowProps {
  readonly row: ExerciseRow;
  /**
   * Where this row stands in the list, counting the head as row one.
   *
   * `aria-rowindex`, because only a window of the catalogue is in the DOM: without it a listener
   * would be told they are on row 3 of a table that says it has 480.
   */
  readonly rowIndex: number;
  /**
   * How far down the list's own box this row is drawn, in pixels. The virtualizer's answer.
   *
   * A `transform` rather than a `top`, so placing a row costs the compositor and not the layout
   * engine — thirty rows re-placed on every scroll frame is the whole cost of a virtual list.
   */
  readonly offset: number;
  readonly selected: boolean;
  /** How many muscle pills this cut of the table fits. `ExerciseTable` owns the two numbers. */
  readonly muscleCap: number;
  /** Whether the table draws its „Tagi" column. */
  readonly tagColumn: boolean;
  /** The signed-in trainer, as two strings — see the header for why it is not the object. */
  readonly trainerInitials: string;
  readonly trainerAvatarUrl: string | null;
  /** `usePointedRow`'s own setter. The row hands it its key. */
  readonly onPoint: (rowKey: string) => void;
  /** Open the exercise card over the list. */
  readonly onOpen: (exerciseId: number) => void;
}

export function ExerciseTableRow({
  row,
  rowIndex,
  offset,
  selected,
  muscleCap,
  tagColumn,
  trainerInitials,
  trainerAvatarUrl,
  onPoint,
  onOpen,
}: ExerciseTableRowProps) {
  const { t } = useTranslation();

  return (
    <div
      role="row"
      aria-rowindex={rowIndex}
      className={[styles.gridRow, styles.row, selected ? styles.rowSelected : '']
        .filter(Boolean)
        .join(' ')}
      style={{ transform: `translateY(${String(offset)}px)` }}
      // See `ExerciseTable`'s header: `mousemove`, and none at all on the row already selected.
      onMouseMove={
        selected
          ? undefined
          : () => {
              onPoint(row.rowKey);
            }
      }
      onFocusCapture={() => {
        onPoint(row.rowKey);
      }}
      // The whole row opens the card — a press on the equipment column means the same thing as a
      // press on the name. `Enter` on the name's own button fires a click that bubbles here, so
      // there is one handler rather than two paths that can diverge.
      onClick={() => {
        onOpen(row.id);
      }}
    >
      {/* `rowheader`, so every other cell in the line announces which movement it is about. The
        * selection BAR is decorative — the panel's heading is the same name. */}
      <span role="rowheader" className={styles.td}>
        {selected && <span className={styles.selectionBar} aria-hidden />}
        <span className={styles.nameCell}>
          <ExerciseStill row={row} className={String(styles.still)} />
          {/* The row's focus target AND its opener. `tabIndex={-1}` because the region above holds
            * the one tab stop and the arrows move focus inside it, so `Tab` walks past a
            * four-hundred-row list in one press. */}
          <button
            type="button"
            data-exercise-row={row.rowKey}
            tabIndex={-1}
            className={styles.name}
            aria-label={t('web:exerciseLibrary.theatre.open', { name: row.name })}
          >
            {row.name}
          </button>
          {/* Board D: neutral words, read with the row's header, never a tinted pill. */}
          {row.needsReview && <span className={styles.review}>{t('web:exerciseLibrary.review.status')}</span>}
        </span>
      </span>

      <span role="cell" className={`${String(styles.td)} ${String(styles.tdCentred)}`}>
        <ExerciseTypeMark row={row} />
      </span>

      <span role="cell" className={styles.td}>
        <ExerciseMuscles muscles={row.muscles} cap={muscleCap} />
      </span>

      <span role="cell" className={styles.td}>
        <ExerciseEquipmentThumbs row={row} />
      </span>

      {tagColumn && (
        <span role="cell" className={styles.td}>
          <ExerciseTags tags={row.tags} />
        </span>
      )}

      <span role="cell" className={`${String(styles.td)} ${String(styles.tdCentred)}`}>
        <ExerciseAuthorMark
          author={row.author}
          initials={trainerInitials}
          avatarUrl={trainerAvatarUrl}
        />
      </span>
    </div>
  );
}
